import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpIcon, FileTextIcon } from 'lucide-react';
import { kordisAnswer, kordisSources } from '../../data/kordis';
import { EASE_OUT } from '../../utils/motion';
export function KordisChatDemo() {
  const [active, setActive] = useState<number | null>(null);
  return <div className="rounded-2xl bg-night p-2.5 md:p-5" data-id="element-581" data-mp="g9nezcxuf7gy">
      <div className="grid overflow-hidden rounded-xl bg-white md:grid-cols-[1fr_270px]" data-id="element-582" data-mp="1cyv1an6adklx">
        <div className="flex min-h-[400px] flex-col p-5 md:p-7" data-id="element-583" data-mp="18x02bpbtvjbb">
          <div className="flex items-center justify-between" data-id="element-584" data-mp="18yunn4r0hdfx">
            <span className="text-sm font-semibold text-ink" data-id="element-585" data-mp="144zvdcxcbq58">Kordis</span>
            <span className="rounded-full bg-surface px-2.5 py-1 text-[11px] font-medium text-fg-muted" data-id="element-586" data-mp="z7gh4d0n7dda">
              Client: Northwind Co.
            </span>
          </div>

          <div className="ml-auto mt-8 max-w-sm rounded-2xl rounded-br-md bg-ink px-4 py-2.5 text-sm text-white" data-id="element-587" data-mp="1wo5ik5lokdm5">
            Why did gross margin drop in March for Northwind?
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 8
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: EASE_OUT,
          delay: 0.25
        }} className="mt-6 flex gap-3" data-id="element-588" data-mp="6997lvga3zcw">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-ink text-xs font-semibold text-white" data-id="element-589" data-mp="1en7iofndt84">
              K
            </span>
            <div className="max-w-lg" data-id="element-590" data-mp="3olfjdclszwqt">
              <p className="text-sm leading-7 text-fg" data-id="element-591" data-mp="3omh468smfxlq">
                {kordisAnswer.map((seg, i) => <React.Fragment key={i} data-id="element-592" data-mp="3amxvnevwek8w" data-mpi={i}>
                    {seg.text}
                    {seg.cite && <button type="button" onMouseEnter={() => setActive(seg.cite ?? null)} onMouseLeave={() => setActive(null)} onFocus={() => setActive(seg.cite ?? null)} onBlur={() => setActive(null)} onClick={() => setActive(active === seg.cite ? null : seg.cite ?? null)} aria-label={`Show source ${seg.cite}`} className={`mx-0.5 inline-grid h-5 min-w-[20px] place-items-center rounded-md px-1 align-[1px] text-[11px] font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 ${active === seg.cite ? 'bg-ink text-white' : 'bg-ink-soft text-ink hover:bg-ink/15'}`} data-id="element-593" data-mp="3eot0gtd1ffrf">
                        {seg.cite}
                      </button>}
                  </React.Fragment>)}
              </p>
              <p className="mt-3 inline-flex items-center gap-2 rounded-lg bg-coral-soft px-2.5 py-1.5 text-xs text-rust" data-id="element-594" data-mp="3o7u0ahj74xmp">
                <span className="font-semibold" data-id="element-595" data-mp="3jehba6aluotf">Moderate confidence</span>
                <span className="text-rust/80" data-id="element-596" data-mp="3qs8h4h3xnteg">· March AP invoices are still syncing</span>
              </p>
            </div>
          </motion.div>

          <div aria-hidden className="mt-auto flex items-center justify-between rounded-xl border border-line px-4 py-3 pt-3 text-sm text-fg-faint" data-id="element-597" data-mp="3vs4n2lw6xuwn">
            Ask about any client…
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-ink text-white" data-id="element-598" data-mp="dkyd5b0exl7g">
              <ArrowUpIcon className="h-4 w-4" data-id="element-599" data-mp="ijrs77vclk9q" />
            </span>
          </div>
        </div>

        <aside aria-label="Sources" className="border-t border-line bg-surface/70 p-5 md:border-l md:border-t-0" data-id="element-600" data-mp="nahwehs1fh2u">
          <p className="text-xs font-semibold text-fg" data-id="element-601" data-mp="sa2xaxqrpzi7">Sources</p>
          <ul className="mt-4 space-y-2" data-id="element-602" data-mp="sbnpigpm16cw">
            {kordisSources.map(source => {
            const isActive = active === source.id;
            return <li key={source.id} className={`flex gap-3 rounded-lg p-3 transition-[background-color,box-shadow] duration-200 ease-out ${isActive ? 'bg-white shadow-sm ring-1 ring-ink/20' : 'bg-transparent'}`} data-id="element-603" data-mp="11b14fowi6n50">
                  <span className={`grid h-5 min-w-[20px] place-items-center rounded-md text-[11px] font-semibold transition-colors duration-200 ${isActive ? 'bg-ink text-white' : 'bg-ink-soft text-ink'}`} data-id="element-604" data-mp="xcu2z5s4xqsn">
                    {source.id}
                  </span>
                  <div className="min-w-0" data-id="element-605" data-mp="emkpv0ut8em6">
                    <p className="flex items-center gap-1.5 truncate text-xs font-medium text-fg" data-id="element-606" data-mp="ennhxc3vcn3o">
                      <FileTextIcon className="h-3.5 w-3.5 shrink-0 text-fg-faint" data-id="element-607" data-mp="9mzm8d4p3i34" />
                      <span className="truncate" data-id="element-608" data-mp="1q0ya7vqcubxg">{source.title}</span>
                    </p>
                    <p className="mt-0.5 text-[11px] text-fg-faint" data-id="element-609" data-mp="1l3prnu1ao5vo">
                      {source.origin} · {source.freshness}
                    </p>
                  </div>
                </li>;
          })}
          </ul>
        </aside>
      </div>
    </div>;
}